// ✅ src/pages/OrderManagement/ProjectManagementDashboard.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaProjectDiagram,
  FaFileInvoiceDollar,
  FaHandshake,
  FaChartLine,
  FaSearch,
  FaEye,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

import ProjectPreOrderLayoutWrapper from "../components/ProjectPreOrderLayoutWrapper";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import HeaderBar from "../components/HeaderBar";

const API_BASE = "https://nissi-india.com/api/dashboard";

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const ProjectManagementDashboard1 = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage helper
  const getLS = (key) =>
    typeof window !== "undefined" ? localStorage.getItem(key) || "" : "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // UI state
  const [searchTerm, setSearchTerm] = useState("");

  // Data states
  const [estimationAlerts, setEstimationAlerts] = useState([]);
  const [tenderAlerts, setTenderAlerts] = useState([]);
  const [projectAlerts, setProjectAlerts] = useState([]);

  const [loading, setLoading] = useState({
    estimation: false,
    tender: false,
    project: false,
  });

  const [error, setError] = useState("");

  const getStatusIcon = (status) => {
    switch (String(status || "").trim()) {
      case "Normal":
        return <FaCheckCircle className="text-green-500" />;
      case "Exceeded":
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaTimesCircle className="text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1";
    switch (String(status || "").trim()) {
      case "Normal":
        return `${baseClasses} bg-green-100 text-green-800 border border-green-300`;
      case "Exceeded":
        return `${baseClasses} bg-red-100 text-red-800 border border-red-300`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 border border-gray-300`;
    }
  };

  const openPopup = (url) => {
    window.open(
      url,
      "_blank",
      "width=900,height=900,top=(screen.height - height)/2,left=(screen.width - width)/2,resizable=no,scrollbars=yes,toolbar=no,status=no,directories=no,position=center"
    );
  };

  const loadAll = async () => {
    setError("");

    setLoading({ estimation: true, tender: true, project: true });

    try {
      const [estRes, tenRes, projRes] = await Promise.all([
        axios.get(`${API_BASE}/estimation`, {
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/tender`, {
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/project`, {
          headers: commonHeaders,
          params: {
            branchtype: branchType,
            branchid: branchId,
          },
        }),
      ]);

      setEstimationAlerts(estRes.data?.list || []);
      setTenderAlerts(tenRes.data?.list || []);
      setProjectAlerts(projRes.data?.list || []);
    } catch (err) {
      console.error("Dashboard load error:", err);
      setError(err?.response?.data?.message || "Failed to load dashboard data");
      setEstimationAlerts([]);
      setTenderAlerts([]);
      setProjectAlerts([]);
    } finally {
      setLoading({ estimation: false, tender: false, project: false });
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived summary cards
  const totalEstimations = useMemo(() => {
    return estimationAlerts.reduce((sum, r) => sum + toNum(r.fld_totestimation), 0);
  }, [estimationAlerts]);

  const totalTenders = useMemo(() => {
    return tenderAlerts.reduce((sum, r) => sum + toNum(r.fld_tottenders), 0);
  }, [tenderAlerts]);

  const totalProjects = useMemo(() => projectAlerts.length, [projectAlerts]);

  const successRate = useMemo(() => {
    const success = tenderAlerts.reduce((s, r) => s + toNum(r.fld_success), 0);
    const total = tenderAlerts.reduce((s, r) => s + toNum(r.fld_tottenders), 0);
    if (!total) return "0%";
    return `${Math.round((success / total) * 100)}%`;
  }, [tenderAlerts]);

  const filteredProjects = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return projectAlerts;
    return projectAlerts.filter((p) => {
      const projectName = String(p.fld_ProjectName || "").toLowerCase();
      const branchName = String(p.fld_branchname || "").toLowerCase();
      return projectName.includes(t) || branchName.includes(t);
    });
  }, [projectAlerts, searchTerm]);

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen transition-all duration-300 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
            : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
        }`}
      >
        <div className="p-6">
          <HeaderBar
            title={
              <span className="font-dmserif text-3xl">
                Project Management Dashboard Alert
              </span>
            }
            setSkin={setSkin}
            currentSkin={skin}
          />
        </div>

        <div
          className={`sticky top-0 z-10 backdrop-blur-md border-b ${
            isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
          }`}
        >
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1
                  className={`text-2xl font-bold flex items-center gap-3 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  <FaProjectDiagram className="text-blue-500" />
                  PROJECT MANAGEMENT DASHBOARD ALERT
                </h1>

                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Monitor and manage all project activities in real-time
                </p>

                <p className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  FinYear: {finYear || "-"} | Branch ID: {branchId || "-"} | Branch Type:{" "}
                  {branchType || "-"} | User ID: {userId || "-"}
                </p>

                {error ? <p className="mt-2 text-sm text-red-500">{error}</p> : null}
              </div>

              <button
                onClick={loadAll}
                className={`px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div
              className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                isDark
                  ? "bg-gradient-to-br from-blue-900/50 to-blue-800/30 backdrop-blur-md border-blue-700 text-white"
                  : "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Estimations</p>
                  <p className="text-3xl font-bold">
                    {loading.estimation ? "..." : totalEstimations}
                  </p>
                </div>
                <FaFileInvoiceDollar className="text-4xl text-blue-200" />
              </div>
            </div>

            <div
              className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                isDark
                  ? "bg-gradient-to-br from-green-900/50 to-green-800/30 backdrop-blur-md border-green-700 text-white"
                  : "bg-gradient-to-br from-green-500 to-green-600 text-white border-green-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Total Tenders</p>
                  <p className="text-3xl font-bold">{loading.tender ? "..." : totalTenders}</p>
                </div>
                <FaHandshake className="text-4xl text-green-200" />
              </div>
            </div>

            <div
              className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                isDark
                  ? "bg-gradient-to-br from-purple-900/50 to-purple-800/30 backdrop-blur-md border-purple-700 text-white"
                  : "bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Total Projects</p>
                  <p className="text-3xl font-bold">
                    {loading.project ? "..." : totalProjects}
                  </p>
                </div>
                <FaProjectDiagram className="text-4xl text-purple-200" />
              </div>
            </div>

            <div
              className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                isDark
                  ? "bg-gradient-to-br from-orange-900/50 to-orange-800/30 backdrop-blur-md border-orange-700 text-white"
                  : "bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">Success Rate</p>
                  <p className="text-3xl font-bold">{loading.tender ? "..." : successRate}</p>
                </div>
                <FaChartLine className="text-4xl text-orange-200" />
              </div>
            </div>
          </div>

          {/* Estimation Alert Section */}
          <div
            className={`rounded-2xl shadow-2xl border overflow-hidden ${
              isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center gap-3 ${
                isDark ? "bg-blue-900/30 border-gray-700" : "bg-blue-600 text-white border-blue-700"
              }`}
            >
              <FaFileInvoiceDollar className="text-2xl" />
              <h2 className="text-xl font-bold">ESTIMATION ALERT</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${isDark ? "bg-gray-700 text-gray-200" : "bg-gray-50 text-gray-700"}`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Branch Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Total No Of Estimation</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Waiting</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Approved</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Total Estimation Cost</th>
                  </tr>
                </thead>

                <tbody
                  className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {loading.estimation ? (
                    <tr>
                      <td colSpan={6} className={`px-6 py-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : estimationAlerts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-6 py-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records found
                      </td>
                    </tr>
                  ) : (
                    estimationAlerts.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:${isDark ? "bg-gray-700" : "bg-blue-50"} transition-colors duration-200`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {index + 1}
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {item.fld_BranchName}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                          {item.fld_totestimation}
                        </td>

                        <td
                          className={`px-6 py-4 text-sm cursor-pointer underline ${
                            isDark ? "text-orange-300" : "text-orange-600"
                          }`}
                          onClick={() => openPopup(`/OrderManagement/EstimationDashBoardReport?status=0`)}
                          title="Open Waiting Estimation Report"
                        >
                          {item.fld_waiting}
                        </td>

                        <td
                          className={`px-6 py-4 text-sm cursor-pointer underline ${
                            isDark ? "text-green-300" : "text-green-600"
                          }`}
                          onClick={() => openPopup(`/OrderManagement/EstimationDashBoardReport?status=1`)}
                          title="Open Approved Estimation Report"
                        >
                          {item.fld_approved}
                        </td>

                        <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-yellow-300" : "text-green-700"}`}>
                          {item.fld_totestimationcost}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tender Alert Section */}
          <div
            className={`rounded-2xl shadow-2xl border overflow-hidden ${
              isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center gap-3 ${
                isDark ? "bg-green-900/30 border-gray-700" : "bg-green-600 text-white border-green-700"
              }`}
            >
              <FaHandshake className="text-2xl" />
              <h2 className="text-xl font-bold">TENDER ALERT</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${isDark ? "bg-gray-700 text-gray-200" : "bg-gray-50 text-gray-700"}`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Branch Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Total No Of Tenders</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Success</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">UnSuccess</th>
                  </tr>
                </thead>

                <tbody
                  className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {loading.tender ? (
                    <tr>
                      <td colSpan={5} className={`px-6 py-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : tenderAlerts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={`px-6 py-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records found
                      </td>
                    </tr>
                  ) : (
                    tenderAlerts.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:${isDark ? "bg-gray-700" : "bg-green-50"} transition-colors duration-200`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {index + 1}
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {item.fld_BranchName}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                          {item.fld_tottenders}
                        </td>

                        <td
                          className={`px-6 py-4 text-sm cursor-pointer underline ${isDark ? "text-green-300" : "text-green-600"}`}
                          onClick={() =>
                            openPopup(
                              `/OrderManagement/TenderDashBoardReport?branchid=${item.fld_Branchid}&status=1`
                            )
                          }
                          title="Open Success Tender Report"
                        >
                          {item.fld_success}
                        </td>

                        <td
                          className={`px-6 py-4 text-sm cursor-pointer underline ${isDark ? "text-red-300" : "text-red-600"}`}
                          onClick={() =>
                            openPopup(
                              `/OrderManagement/TenderDashBoardReport?branchid=${item.fld_Branchid}&status=0`
                            )
                          }
                          title="Open UnSuccess Tender Report"
                        >
                          {item.fld_unsuccess}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Project Alert Section */}
          <div
            className={`rounded-2xl shadow-2xl border overflow-hidden ${
              isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center justify-between ${
                isDark ? "bg-purple-900/30 border-gray-700" : "bg-purple-600 text-white border-purple-700"
              }`}
            >
              <div className="flex items-center gap-3">
                <FaProjectDiagram className="text-2xl" />
                <h2 className="text-xl font-bold">PROJECT ALERT</h2>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`pl-10 pr-4 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 ${
                      isDark
                        ? "bg-white/10 backdrop-blur-md border-white/20 text-white placeholder-white/60 focus:ring-white/30"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-purple-300"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${isDark ? "bg-gray-700 text-gray-200" : "bg-gray-50 text-gray-700"}`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Branch Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Project Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Estimation Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Current Expenses</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">More Details</th>
                  </tr>
                </thead>

                <tbody
                  className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  }`}
                >
                  {loading.project ? (
                    <tr>
                      <td colSpan={7} className={`px-6 py-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : filteredProjects.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-6 py-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records found
                      </td>
                    </tr>
                  ) : (
                    filteredProjects.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:${isDark ? "bg-gray-700" : "bg-purple-50"} transition-colors duration-200`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {index + 1}
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {item.fld_branchname}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-blue-300" : "text-purple-700"}`}>
                          <div className="max-w-xs truncate" title={item.fld_ProjectName}>
                            {item.fld_ProjectName}
                          </div>
                        </td>

                        <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-300" : "text-green-700"}`}>
                          {item.fld_estimationvalue}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-orange-300" : "text-orange-600"}`}>
                          {item.fld_expenses}
                        </td>

                        <td className="px-6 py-4">
                          <span className={getStatusBadge(item.fld_status)}>
                            {getStatusIcon(item.fld_status)}
                            {item.fld_status}
                          </span>
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              const w = window.screen.availWidth;
                              const h = window.screen.availHeight;

                              window.open(
                                `/OrderManagement/BOQ?contractid=${item.fld_contractid}`,
                                "_blank",
                                `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${w},height=${h},top=0,left=0`
                              );
                            }}
                            className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 ${
                              isDark
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                            }`}
                          >
                            <FaEye className="inline mr-1" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default ProjectManagementDashboard1;